import React, { type KeyboardEvent, useEffect, useRef } from 'react';
import { useStyletron } from 'baseui';
import { KIND as TAG_KIND } from 'baseui/tag';
import { useAtomValue } from 'jotai';
import { colorWithOpacity } from '../../../common/utils/color-with-opacity';
import { annotationMetadataAtom } from '../../../image/labelling-v2/atoms/annotation-atoms';
import { allAnnotationsDataWithFrameRangesAtom, relationshipStoreWithLabelDataAtom } from '../../../image/labelling-v2/atoms/relationship-atoms';
import { annotationIdToSpanAtom } from '../../../image/labelling-v2/atoms/side-panel-atoms';
import { getDynamicAttributes, getFrameRangeLabel, getRelationshipAttributes } from '../../../image/labelling-v2/components/side-panel/utils';
import { AnnotationSubContent } from '../../side-panel/annotation-details/annotation-sub-content';
import type { AnnotationInfoT, HeaderInfoT } from '../../side-panel/annotations/types';
import { getLabelIcon } from '../../side-panel/annotations-common/utils';
import { SIDE_PANEL_ACTIONS } from '../../side-panel/const';
import { useDispatch } from '../../side-panel/hooks/use-dispatch';
import { useSidePanel } from '../../side-panel/side-panel-provider';
import { useAnnotationExpansion } from './hooks/use-annotation-expansion';
import { AnnotationAccordion } from './annotation-accordion';
import { AnnotationHeader } from './annotation-header';
const HOVERED_BACKGROUND_OPACITY = 0.6;
const getAnnotationBackgroundColor = ({
  isSelected,
  isHovered,
  backgroundColor
}: {
  isSelected: boolean;
  isHovered: boolean;
  backgroundColor: string;
}) => {
  if (isSelected) {
    return backgroundColor;
  }
  if (isHovered) {
    return colorWithOpacity(backgroundColor, HOVERED_BACKGROUND_OPACITY);
  }
  return 'transparent';
};
type PropsT = {
  annotations: AnnotationInfoT[];
  iconConfig: HeaderInfoT['iconConfig'];
  iconSize?: number;
  selectedAnnotationIds: string[];
  focusedAnnotationId?: string;
  labelColor?: string;
  onDeleteAnnotation: (annotation: AnnotationInfoT) => void;
  onMouseEnter: (annotationId: string | null) => void;
  onMouseLeave: (annotationId: string | null) => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>, annotationId: string) => void;
};
export const AnnotationSectionContent = ({
  annotations,
  iconConfig,
  iconSize,
  selectedAnnotationIds,
  focusedAnnotationId,
  labelColor,
  onDeleteAnnotation,
  onMouseEnter,
  onMouseLeave,
  onKeyDown
}: PropsT) => {
  const {
    toolConfig
  } = useSidePanel();
  const {
    expandedKeys,
    onIconClick,
    onNameClick
  } = useAnnotationExpansion({
    annotationIds: annotations.map(annotation => annotation.id)
  });
  const selectedSectionRef = useRef<HTMLDivElement>(null);
  const [, theme] = useStyletron();
  const dispatch = useDispatch();
  const selectedAnnotationKey = selectedAnnotationIds.join('|');
  useEffect(() => {
    selectedSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end'
    });
  }, [selectedAnnotationKey]);
  const annotationMetadata = useAtomValue(annotationMetadataAtom);
  const annotationIdToSpan = useAtomValue(annotationIdToSpanAtom);
  const relationshipStore = useAtomValue(relationshipStoreWithLabelDataAtom);
  const allAnnotationsWithFrameRanges = useAtomValue(allAnnotationsDataWithFrameRangesAtom);
  const sections = annotations.map(annotation => {
    const annotationId = annotation.id;
    const isSelected = selectedAnnotationIds.includes(annotationId);
    const isDisabled = annotation.isDisabled;
    const isHovered = focusedAnnotationId === annotationId;
    const annotationInfo: AnnotationInfoT = {
      ...annotation,
      isSelected
    };
    const dynamicAttributeTags = getDynamicAttributes(annotationId, annotationIdToSpan[annotationId], annotationMetadata.get(annotationId)?.dynamicAnswers ?? [], {
      includeAttributeIndex: false
    }).map(({
      key,
      value
    }) => ({
      label: `${key}: ${value}`,
      kind: TAG_KIND.accent,
      truncate: false,
      color: labelColor
    }));
    const relationshipTags = getRelationshipAttributes(annotationId, relationshipStore, allAnnotationsWithFrameRanges).map(({
      relationshipLabelName,
      targetAnnotationName,
      frameRanges
    }) => {
      const frameRangeLabel = frameRanges.map(({
        startFrameIndex,
        endFrameIndex
      }) => getFrameRangeLabel({
        start: startFrameIndex,
        end: endFrameIndex
      }, {
        collapseSingleFrame: true
      })).join(', ');
      return {
        label: `${relationshipLabelName} -> ${targetAnnotationName}${frameRangeLabel ? `: ${frameRangeLabel}` : ''}`,
        kind: TAG_KIND.accent,
        truncate: false,
        color: labelColor
      };
    });
    const annotationAttributes = annotationInfo.attributes.map(attribute => ({
      ...attribute,
      color: attribute.kind === TAG_KIND.neutral ? attribute.color : attribute.color ?? labelColor
    }));
    const annotationDetailsInfo: AnnotationInfoT = {
      ...annotationInfo,
      attributes: [...annotationAttributes, ...dynamicAttributeTags, ...relationshipTags]
    };
    const rowIconKey = iconConfig?.iconKey || annotation.annotationType || '';
    const rowIcon = getLabelIcon({
      color: iconConfig?.color ?? labelColor ?? '',
      iconKey: rowIconKey
    }, toolConfig, iconSize);
    const hoverProps = isDisabled ? {} : {
      onMouseEnter: () => {
        onMouseEnter(annotationId);
      },
      onMouseLeave: () => {
        onMouseLeave(null);
      }
    };
    const annotationBackgroundColor = getAnnotationBackgroundColor({
      isSelected,
      isHovered,
      backgroundColor: theme.colors.backgroundSecondary
    });
    const handleNameClick = (event: React.MouseEvent) => {
      if (isDisabled) return;
      event.stopPropagation();
      dispatch({
        type: SIDE_PANEL_ACTIONS.SELECT_ANNOTATION,
        annotationId
      });
      onNameClick(annotationId);
    };
    return {
      id: annotationId,
      isSelected,
      containerRef: isSelected ? selectedSectionRef : undefined,
      containerBackgroundColor: annotationBackgroundColor,
      header: <div {...hoverProps} style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center'
      }} role="button" tabIndex={isDisabled ? -1 : 0} onKeyDown={isDisabled ? undefined : event => onKeyDown(event, annotationId)} onClick={handleNameClick}>
          <AnnotationHeader annotation={annotationInfo} icon={rowIcon} handleDeleteAnnotation={() => onDeleteAnnotation(annotationInfo)} />
        </div>,
      items: [<div key={annotationId} {...hoverProps} data-testid={`annotation-${annotationId}`} style={{
        padding: `${theme.sizing.scale300} ${theme.sizing.scale500}`
      }}>
          <AnnotationSubContent annotation={annotationDetailsInfo} />
        </div>]
    };
  });
  return <AnnotationAccordion sections={sections} expandedKeys={expandedKeys} onChange={keys => {
    const newKey = keys.find(k => !expandedKeys.includes(k));
    const removedKey = expandedKeys.find(k => !keys.includes(k));
    if (newKey !== undefined) onIconClick(newKey);
    if (removedKey !== undefined) onIconClick(removedKey);
  }} />;
};